import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Store, ShoppingBag, Truck, Users, MessageCircle, Megaphone,
  BarChart3, LogOut, Menu as MenuIcon, X, Crown, Wallet, Settings, CircleUser, Trophy, FileBarChart2, Shield,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { FranchiseeNotificationsBell } from "../components/FranchiseeNotificationsBell";
import { FranchiseeSupportButton } from "../components/FranchiseeSupportButton";
import useOnboardingTour from "../hooks/useOnboardingTour";
import useUnreadChats from "../hooks/useUnreadChats";
import { ChatBadge } from "../components/ChatBadge";
import { FRANQUEADO_STEPS } from "../components/onboardingSteps";
import { API } from "../App";

const NAV = [
  { to: "/franquia/painel", label: "Painel", icon: LayoutDashboard, testId: "fr-nav-dashboard" },
  { to: "/franquia/lojas", label: "Lojas", icon: Store, testId: "fr-nav-stores" },
  { to: "/franquia/pedidos", label: "Pedidos", icon: ShoppingBag, testId: "fr-nav-orders" },
  { to: "/franquia/entregadores", label: "Entregadores", icon: Truck, testId: "fr-nav-drivers" },
  { to: "/franquia/clientes", label: "Clientes", icon: Users, testId: "fr-nav-customers" },
  { to: "/franquia/financeiro", label: "Financeiro", icon: Wallet, testId: "fr-nav-finance" },
  { to: "/franquia/relatorios", label: "Relatórios", icon: FileBarChart2, testId: "fr-nav-reports" },
  { to: "/franquia/analytics", label: "Analytics", icon: BarChart3, testId: "fr-nav-analytics" },
  { to: "/franquia/rankings", label: "Rankings", icon: Trophy, testId: "fr-nav-rankings" },
  { to: "/franquia/chat-pro", label: "Chat", icon: MessageCircle, testId: "fr-nav-chat", chatBadge: true },
  { to: "/franquia/comunicados", label: "Comunicados", icon: Megaphone, testId: "fr-nav-broadcasts" },
  { to: "/franquia/auditoria", label: "Auditoria", icon: Shield, testId: "fr-nav-audit" },
  { to: "/franquia/configuracoes", label: "Configurações", icon: Settings, testId: "fr-nav-settings" },
  { to: "/franquia/perfil", label: "Perfil", icon: CircleUser, testId: "fr-nav-profile" },
];

const fmtBRL = (n) => `R$ ${Number(n || 0).toFixed(2).replace(".", ",")}`;

function LivePill({ dotClass, label, value, testId }) {
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10" data-testid={testId}>
      <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />
      <span className="text-[11px] font-extrabold text-white tabular-nums">{value}</span>
      <span className="text-[10px] text-white/50 font-bold">{label}</span>
    </div>
  );
}

/**
 * Premium dark SaaS shell for the Franchisee module.
 * Collapsible sidebar + topbar with real-time city stats.
 */
export default function FranchiseeShell({ user, logout, children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [live, setLive] = useState(null);
  const [clock, setClock] = useState(() => new Date());
  const { total: unreadTotal } = useUnreadChats();

  useEffect(() => {
    const v = localStorage.getItem("fr_sidebar_collapsed");
    if (v === "1") setCollapsed(true);
  }, []);
  const toggleCollapsed = () => {
    setCollapsed((c) => {
      try { localStorage.setItem("fr_sidebar_collapsed", c ? "0" : "1"); } catch {}
      return !c;
    });
  };

  const fetchLive = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API}/franchisee/live`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setLive(await r.json());
    } catch (e) { console.warn("live stats", e); }
  }, []);

  useEffect(() => {
    fetchLive();
    const iv = setInterval(fetchLive, 15000);
    return () => clearInterval(iv);
  }, [fetchLive]);

  useEffect(() => {
    const iv = setInterval(() => setClock(new Date()), 30000);
    return () => clearInterval(iv);
  }, []);

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");
  const handleLogout = () => { logout(); navigate("/login"); };

  const cityLabel = live?.city ? `${live.city}` : "Franquia";

  return (
    <div className="min-h-screen bg-[#0B0D0F] text-white" data-testid="franchisee-shell">
      {/* Sidebar (desktop) */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 bg-[#13161A] border-r border-white/5 hidden md:flex flex-col transition-all duration-200 ${collapsed ? "w-16" : "w-60"}`}
        data-testid="fr-sidebar"
      >
        <div className="h-16 px-4 flex items-center justify-between border-b border-white/5">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF6B00] to-[#F43F5E] flex items-center justify-center shadow-[0_0_16px_rgba(255,107,0,0.4)]">
                <Crown className="w-6 h-6 text-white" strokeWidth={2.4} />
              </div>
              <div className="leading-tight">
                <span className="font-extrabold tracking-tight block">GoFood</span>
                <span className="text-[9px] uppercase tracking-widest text-orange-400 font-extrabold">Franquia</span>
              </div>
            </div>
          )}
          <button
            onClick={toggleCollapsed}
            className="w-10 h-10 rounded-lg hover:bg-white/5 flex items-center justify-center"
            data-testid="fr-sidebar-collapse"
            aria-label="Recolher sidebar"
          >
            <MenuIcon className="w-6 h-6 text-white/70" strokeWidth={2.4} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold transition-all ${
                  active
                    ? "text-white bg-[#FF6B00]/15 shadow-[inset_0_0_0_1px_rgba(255,107,0,0.35),0_0_24px_rgba(255,107,0,0.15)]"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                }`}
                data-testid={item.testId}
                title={collapsed ? item.label : undefined}
              >
                {active && (
                  <motion.div
                    layoutId="fr-nav-pill"
                    className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-[#FF8A3D] shadow-[0_0_12px_rgba(255,138,61,0.7)]"
                  />
                )}
                <Icon className={`w-6 h-6 shrink-0 ${active ? "text-[#FF8A3D]" : ""}`} strokeWidth={active ? 2.6 : 2.2} />
                {!collapsed && <span className="flex-1">{item.label}</span>}
                {item.chatBadge && unreadTotal > 0 && (
                  <ChatBadge count={unreadTotal} className={collapsed ? "absolute top-1 right-1" : ""} testId="fr-chat-badge" />
                )}
              </Link>
            );
          })}
        </nav>

        <div className="px-2 pb-3 border-t border-white/5 pt-3">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold text-white/60 hover:text-[#FF4D4F] hover:bg-[#FF4D4F]/10"
            data-testid="fr-logout"
          >
            <LogOut className="w-6 h-6 shrink-0" strokeWidth={2.4} />
            {!collapsed && <span>Sair</span>}
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/60 md:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 360, damping: 30 }}
              className="fixed inset-y-0 left-0 z-50 w-64 bg-[#13161A] border-r border-white/5 md:hidden"
              data-testid="fr-mobile-menu"
            >
              <div className="h-16 px-4 flex items-center justify-between border-b border-white/5">
                <span className="font-extrabold">GoFood <span className="text-orange-400 text-xs">Franquia</span></span>
                <button onClick={() => setMobileOpen(false)} className="w-10 h-10 rounded-lg hover:bg-white/5 flex items-center justify-center">
                  <X className="w-6 h-6 text-white/70" strokeWidth={2.5} />
                </button>
              </div>
              <nav className="py-3 px-2 space-y-1">
                {NAV.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.to);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold ${active ? "text-white bg-[#FF6B00]/15" : "text-white/60 hover:text-white hover:bg-white/5"}`}
                    >
                      <Icon className="w-6 h-6" strokeWidth={2.4} />
                      <span className="flex-1">{item.label}</span>
                      {item.chatBadge && unreadTotal > 0 && <ChatBadge count={unreadTotal} testId="fr-chat-badge-mobile" />}
                    </Link>
                  );
                })}
                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold text-[#FF4D4F]">
                  <LogOut className="w-6 h-6" strokeWidth={2.4} /> Sair
                </button>
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main area */}
      <div className={`min-h-screen flex flex-col transition-all ${collapsed ? "md:pl-16" : "md:pl-60"}`}>
        {/* Topbar */}
        <header
          className="sticky top-0 z-20 h-16 bg-[#0B0D0F]/85 backdrop-blur-xl border-b border-white/5 flex items-center gap-3 px-4"
          data-testid="fr-topbar"
        >
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden w-11 h-11 rounded-lg hover:bg-white/5 flex items-center justify-center"
            data-testid="fr-mobile-menu-btn"
            aria-label="Abrir menu"
          >
            <MenuIcon className="w-7 h-7 text-white/80" strokeWidth={2.5} />
          </button>

          <div className="min-w-0">
            <p className="text-[9px] uppercase tracking-widest text-orange-400 font-extrabold leading-none">Centro de Controle</p>
            <p className="text-sm font-extrabold capitalize truncate" data-testid="fr-topbar-city">
              {cityLabel} {live?.state ? <span className="text-white/40 text-xs uppercase">· {live.state}</span> : null}
            </p>
          </div>

          <div className="flex-1" />

          {live && (
            <div className="hidden lg:flex items-center gap-2" data-testid="fr-topbar-live">
              <LivePill dotClass="bg-orange-400" value={live.orders_today} label="pedidos hoje" testId="fr-live-orders" />
              <LivePill dotClass="bg-emerald-400" value={fmtBRL(live.revenue_today)} label="faturamento" testId="fr-live-revenue" />
              <LivePill dotClass="bg-teal-400" value={fmtBRL(live.commission_today)} label="comissão" testId="fr-live-commission" />
              <LivePill dotClass="bg-violet-400" value={live.merchants_active} label="lojas ativas" testId="fr-live-stores" />
              <LivePill dotClass="bg-emerald-400 animate-pulse" value={live.drivers_online} label="online" testId="fr-live-drivers" />
              <div className="hidden xl:flex items-center gap-2">
                <LivePill dotClass="bg-amber-400" value={live.pending_now} label="pendentes" testId="fr-live-pending" />
                <LivePill dotClass="bg-sky-400" value={live.in_delivery_now} label="em rota" testId="fr-live-route" />
              </div>
            </div>
          )}

          <FranchiseeNotificationsBell />

          <div className="hidden sm:block px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] font-extrabold tabular-nums" data-testid="fr-topbar-clock">
            {clock.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
          </div>

          <div className="flex items-center gap-2 pl-2 border-l border-white/10">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF6B00] to-[#F43F5E] flex items-center justify-center text-xs font-extrabold shrink-0">
              {(user?.name || "F")[0].toUpperCase()}
            </div>
            <span className="hidden md:block text-xs font-bold text-white/80 max-w-[120px] truncate">{user?.name || "Franqueado"}</span>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6 max-w-7xl w-full mx-auto" data-testid="fr-main">
          {children}
        </main>
      </div>

      <FranchiseeSupportButton />
      {useOnboardingTour({ user, apiBase: API, steps: FRANQUEADO_STEPS, brandColor: "#FF6B00" })}
    </div>
  );
}
